import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getIncidencias, getIncidenciasStats, getAnuncios, getReservas, getDocumentos, getActividad } from '../services/api';
import './DashboardVecino.css';

const DashboardIcon = ({ name }) => {
  const common = {
    width: 18,
    height: 18,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round'
  };

  const icons = {
    incidencias: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
    tablon: <><path d="M3 6h18" /><path d="M7 12h10" /><path d="M10 18h4" /></>,
    reservas: <><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4" /><path d="M8 2v4" /><path d="M3 10h18" /></>,
    documentos: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></>,
    actividad: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 3" /></>,
    usuario: <><circle cx="12" cy="8" r="4" /><path d="M5 20a7 7 0 0 1 14 0" /></>,
    zona: <><path d="M3 10.5 12 3l9 7.5" /><path d="M5 9.5V21h14V9.5" /></>,
    usuarios: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
    comunidades: <><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></>,
    proveedores: <><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a2 2 0 0 0-4 0v2" /><path d="M12 7V5a2 2 0 0 0-4 0v2" /><line x1="12" y1="12" x2="12" y2="16" /><line x1="10" y1="14" x2="14" y2="14" /></>,
    permisos: <><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>
  };

  return <svg {...common}>{icons[name] || icons.actividad}</svg>;
};

function DashboardVecino({ user }) {
  const navigate = useNavigate();
  // Estados para datos
  const [incidencias, setIncidencias] = useState([]);
  const [statsIncidencias, setStatsIncidencias] = useState({ pendiente: 0, asignada: 0, en_proceso: 0, resuelta: 0, total: 0 });
  const [anuncios, setAnuncios] = useState([]);
  const [reservas, setReservas] = useState([]);
  const [documentos, setDocumentos] = useState([]);
  const [actividad, setActividad] = useState([]);
  const [loading, setLoading] = useState(true);

  // Cargar todos los datos al montar componente
  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const [
        incRes,
        statsRes,
        anuncRes,
        resRes,
        docRes,
        actRes
      ] = await Promise.all([
        getIncidencias({ per_page: 20 }).catch(() => ({ data: [] })),
        getIncidenciasStats().catch(() => ({ data: { pendiente: 0, asignada: 0, en_proceso: 0, resuelta: 0, total: 0 } })),
        getAnuncios().catch(() => ({ data: [] })),
        getReservas().catch(() => ({ data: [] })),
        getDocumentos().catch(() => ({ data: [] })),
        getActividad().catch(() => ({ data: [] }))
      ]);

      setIncidencias(incRes.data?.items || incRes.data || []);
      setStatsIncidencias(statsRes.data || { pendiente: 0, asignada: 0, en_proceso: 0, resuelta: 0, total: 0 });
      setAnuncios(anuncRes.data?.items || anuncRes.data || []);
      setReservas(resRes.data?.items || resRes.data || []);
      setDocumentos(docRes.data?.items || docRes.data || []);
      setActividad((actRes.data?.items || actRes.data || []).slice(0, 5));
    } catch (error) {
      console.error('Error cargando datos del dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calcular métricas — contadores desde endpoint dedicado (sin límite de paginación)
  const incidenciasPendientes = (statsIncidencias.pendiente || 0) + (statsIncidencias.asignada || 0);
  const incidenciasEnProceso = statsIncidencias.en_proceso || 0;
  const totalNoResueltas = incidenciasPendientes + incidenciasEnProceso;

  const incidenciasUrgentes = incidencias.filter(
    i => i.prioridad === 'alta' && i.estado !== 'resuelta'
  ).length;
  
  const avisosImportantes = anuncios
    .slice()
    .sort((a, b) => new Date(b.fecha_creacion) - new Date(a.fecha_creacion))
    .slice(0, 3);

  const proximasReservas = reservas
    .filter(r => new Date(r.fecha) >= new Date(new Date().setHours(0, 0, 0, 0)))
    .sort((a, b) => new Date(a.fecha) - new Date(b.fecha))
    .slice(0, 3);
  
  const proximaReserva = reservas.length > 0
    ? reservas.sort((a, b) => new Date(a.fecha) - new Date(b.fecha))[0]
    : null;

  // Saludo dinámico según hora
  const getSaludo = () => {
    const hora = new Date().getHours();
    if (hora >= 7 && hora < 14) return { texto: 'Buenos días', emoji: '☀️' };
    if (hora >= 14 && hora < 21) return { texto: 'Buenas tardes', emoji: '🌤️' };
    return { texto: 'Buenas noches', emoji: '🌙' };
  };

  const saludo = getSaludo();
  const nombreUsuario = user?.nombre || 'Vecino/a';
  const nombreComunidad = user?.comunidad_nombre || user?.comunidad || 'Tu comunidad';

  const isAdmin = user?.rol === 'administrador' || user?.rol === 'empleado' || user?.rol === 'presidente';

  // Estados de carga (skeleton)
  if (loading) {
    return (
      <div className="dashboard-vecino-container">
        <div className="dashboard-vecino__header">
          <div className="skeleton-title"></div>
          <div className="skeleton-text"></div>
        </div>
        <div className="dashboard-vecino__skeleton-grid">
          {[1, 2, 3].map(i => (
            <div key={i} className="skeleton-card"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-vecino-container">
      <section className="dashboard-vecino__context-header">
        <div className="dashboard-vecino__context-left">
          <h1 className="dashboard-vecino__context-title">
            {saludo.emoji} {saludo.texto}, {nombreUsuario}
          </h1>
          <p className="dashboard-vecino__context-community">
            {isAdmin ? 'Gestiona y disfruta todas tus comunidades desde aquí.' : `Nos alegra tenerte en ${nombreComunidad}.`}
          </p>
        </div>

      </section>

      <section className="dashboard-vecino__hero-card">
        <div className="dashboard-vecino__hero-header">
          <div>
            <p className="dashboard-vecino__hero-kicker">Estado de la comunidad</p>
            <h2 className="dashboard-vecino__hero-title">Resumen de incidencias</h2>
          </div>
          <button
            type="button"
            className="dashboard-vecino__hero-link"
            onClick={() => navigate('/incidencias')}
          >
            Ver todas
          </button>
        </div>

        <div className="dashboard-vecino__hero-metrics">
          <div className="dashboard-vecino__hero-metric">
            <div className="dashboard-vecino__hero-metric-left">
              <span className="dashboard-vecino__hero-metric-icon">🟡</span>
              <span className="dashboard-vecino__hero-metric-label">Pendientes</span>
            </div>
            <span className="dashboard-vecino__hero-metric-value">{incidenciasPendientes}</span>
          </div>
          <div className="dashboard-vecino__hero-metric">
            <div className="dashboard-vecino__hero-metric-left">
              <span className="dashboard-vecino__hero-metric-icon">⏱️</span>
              <span className="dashboard-vecino__hero-metric-label">En proceso</span>
            </div>
            <span className="dashboard-vecino__hero-metric-value">{incidenciasEnProceso}</span>
          </div>
        </div>
      </section>

      <section className="dashboard-vecino__two-columns">
        <article className="dashboard-vecino__panel-card">
          <div className="dashboard-vecino__panel-head">
            <h3 className="dashboard-vecino__section-title">Avisos importantes</h3>
            <button type="button" className="dashboard-vecino__panel-link" onClick={() => navigate('/tablon')}>
              Ver todos
            </button>
          </div>

          {avisosImportantes.length === 0 ? (
            <p className="dashboard-vecino__empty-text">Sin avisos recientes</p>
          ) : (
            <div className="dashboard-vecino__stack-list">
              {avisosImportantes.map((anuncio) => (
                <button
                  type="button"
                  key={anuncio.id}
                  className="dashboard-vecino__stack-item"
                  onClick={() => navigate(`/tablon/${anuncio.id}`)}
                >
                  <span className="dashboard-vecino__stack-title">{anuncio.titulo}</span>
                  <span className="dashboard-vecino__stack-meta">
                    {new Date(anuncio.fecha_creacion).toLocaleDateString('es-ES', {
                      day: 'numeric',
                      month: 'short'
                    })}
                  </span>
                </button>
              ))}
            </div>
          )}
        </article>

        <article className="dashboard-vecino__panel-card">
          <div className="dashboard-vecino__panel-head">
            <h3 className="dashboard-vecino__section-title">Próximas reservas</h3>
            <button type="button" className="dashboard-vecino__panel-link" onClick={() => navigate('/reservas')}>
              Ver agenda
            </button>
          </div>

          {proximasReservas.length === 0 ? (
            <p className="dashboard-vecino__empty-text">No hay reservas próximas</p>
          ) : (
            <div className="dashboard-vecino__agenda-list">
              {proximasReservas.map((reserva) => (
                <button
                  type="button"
                  key={reserva.id}
                  className="dashboard-vecino__agenda-item"
                  onClick={() => navigate('/reservas')}
                >
                  <span className="dashboard-vecino__agenda-date">
                    {new Date(reserva.fecha).toLocaleDateString('es-ES', {
                      day: 'numeric',
                      month: 'short'
                    })}
                  </span>
                  <span className="dashboard-vecino__agenda-content">
                    <span className="dashboard-vecino__agenda-zone">{reserva.zona_nombre || 'Zona común'}</span>
                    <span className="dashboard-vecino__agenda-time">
                      {reserva.hora_inicio && reserva.hora_fin ? `${reserva.hora_inicio} - ${reserva.hora_fin}` : 'Horario por confirmar'}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </article>
      </section>

      <section className="dashboard-vecino__panel-card">
        <div className="dashboard-vecino__panel-head">
          <h3 className="dashboard-vecino__section-title">Actividad reciente</h3>
          <span className="dashboard-vecino__panel-pill">{actividad.length} movimientos</span>
        </div>

        {actividad.length === 0 ? (
          <p className="dashboard-vecino__empty-text">No hay actividad reciente en tu comunidad</p>
        ) : (
          <div className="dashboard-vecino__activity-list-clean">
            {actividad.map((item) => {
              const tipo = (item.tipo || '').toLowerCase();
              let iconName = 'actividad';
              let linkTo = null;

              if (tipo.includes('incidencia')) {
                iconName = 'incidencias';
                linkTo = item.referencia_id ? `/incidencias/${item.referencia_id}` : '/incidencias';
              } else if (tipo.includes('documento')) {
                iconName = 'documentos';
                linkTo = '/documentos';
              } else if (tipo.includes('anuncio')) {
                iconName = 'tablon';
                linkTo = item.referencia_id ? `/tablon/${item.referencia_id}` : '/tablon';
              } else if (tipo.includes('reserva')) {
                iconName = 'reservas';
                linkTo = '/reservas';
              }

              return (
                <button
                  key={item.id}
                  type="button"
                  className="dashboard-vecino__activity-row"
                  onClick={() => linkTo && navigate(linkTo)}
                >
                  <span className="dashboard-vecino__activity-row-icon">
                    <DashboardIcon name={iconName} />
                  </span>
                  <span className="dashboard-vecino__activity-row-content">
                    <span className="dashboard-vecino__activity-row-text">{item.descripcion}</span>
                    <span className="dashboard-vecino__activity-row-meta">
                      {item.usuario_nombre ? item.usuario_nombre.trim() : 'Comunidad'}
                      {' · '}
                      {item.fecha_creacion
                        ? new Date(item.fecha_creacion).toLocaleString('es-ES', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })
                        : ''}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* ============ EMPTY STATE ============ */}
      {!loading && actividad.length === 0 && totalNoResueltas === 0 && 
       anuncios.length === 0 && !proximaReserva && documentos.length === 0 && (
        <div className="dashboard-vecino__welcome">
          <div className="dashboard-vecino__welcome-illustration">
            <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="60" cy="60" r="60" fill="#f0f4ff"/>
              {/* House */}
              <path d="M30 55L60 30L90 55V90H30V55Z" fill="#c7d2fe" stroke="#6366f1" strokeWidth="2.5" strokeLinejoin="round"/>
              {/* Door */}
              <rect x="50" y="72" width="20" height="18" rx="3" fill="#6366f1" opacity="0.7"/>
              {/* Windows */}
              <rect x="36" y="60" width="14" height="12" rx="2" fill="#fff" stroke="#6366f1" strokeWidth="1.5"/>
              <rect x="70" y="60" width="14" height="12" rx="2" fill="#fff" stroke="#6366f1" strokeWidth="1.5"/>
              {/* Chimney */}
              <rect x="72" y="34" width="8" height="14" rx="2" fill="#818cf8"/>
              {/* Stars */}
              <circle cx="22" cy="28" r="3" fill="#a5b4fc" opacity="0.6"/>
              <circle cx="96" cy="22" r="2" fill="#818cf8" opacity="0.7"/>
              <circle cx="105" cy="45" r="2.5" fill="#a5b4fc" opacity="0.5"/>
              <circle cx="15" cy="55" r="2" fill="#c7d2fe" opacity="0.7"/>
            </svg>
          </div>
          <h2 className="dashboard-vecino__welcome-title">Todo tranquilo por aquí</h2>
          <p className="dashboard-vecino__welcome-text">
            Tu comunidad está al día. Cuando haya novedades, anuncios o incidencias, aparecerán aquí.
          </p>
          <div className="dashboard-vecino__welcome-actions">
            <button type="button" className="dashboard-vecino__welcome-btn dashboard-vecino__welcome-btn--primary" onClick={() => navigate('/incidencias/nueva')}>
              + Nueva Incidencia
            </button>
            <button type="button" className="dashboard-vecino__welcome-btn dashboard-vecino__welcome-btn--secondary" onClick={() => navigate('/reservas')}>
              Hacer Reserva
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardVecino;
